import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { EndpointService } from '@app/core';
import { ToastrService } from 'ngx-toastr';

export interface Parametro {
  alias: string;
  id?: number;
  consecutivo: number;
  consulta?: number;
  tipo: number;
  digitos?: number;
  accion?: string;
}

export interface EstructuraRadicado {
  id?: number;
  nombre?: string;
  estructura?: string;
  parametros?: Parametro[];
  detalle?: Detalles[];
}

export interface Consecutive {
  id?: number;
  dependencia?: number;
  consecutivo: number;
  periodicidad: number;
  tipo_periodicidad: string;
  fecha_reinicio: Date;
  reinicio: boolean;
  detail?: Detalles[];
}

export interface Detalles {
  id: number;
  id_serie: number;
  serie: string;
  id_subserie: string;
  subserie: string;
  id_tipo_documental: number;
  tipo_documental: string;
}

@Injectable()
export class FilingStructureDataService{

  constructor (
    private http: HttpClient,
    private api: EndpointService, 
    private toastr: ToastrService
  ) { }

  
   /**
    *Consultar las estructuras de radicados
    *
    * @return {*}  {Observable<SeriePredeterminada[]>}
    * @memberof FilingStructureDataService
    */
   getStructures(): Observable<EstructuraRadicado[]> {
    return new Observable(obs => {
      this.http.get<EstructuraRadicado[]>(this.api.get('estructure')).subscribe(
        structures => {
        obs.next(structures);
      },
      err => {
        obs.error(err);
      });
    });
  }

  
   /**
    *Obtiene los consecutivos para llenar el selector
    *
    * @return {*}  {Observable<Consecutive[]>}
    * @memberof FilingStructureDataService
    */
   getConsecutives(): Observable<Consecutive[]> {
    return new Observable((obs) => {
      this.http.get<Consecutive[]>(this.api.get("consecutives")).subscribe(
        (consecutive) => {
          obs.next(consecutive);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

   /**
    *Obtiene las consultas disponibles para llenar el selector
    *
    * @return {*}  {Observable<Consecutive[]>}
    * @memberof FilingStructureDataService
    */
    getSearchStructure(): Observable<any[]> {
      return new Observable((obs) => {
        this.http.get<any[]>(this.api.get("searchsStructureRadication")).subscribe(
          (search) => {
            obs.next(search);
          },
          (err) => {
            obs.error(err);
          }
        );
      });
    }

  /**
   *Agrega una nueva estructura de radicado
   *
   * @param {Consecutive} consecutive
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  addStructure(structure: EstructuraRadicado): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<boolean>(this.api.get("estructure"), structure).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Estructura agregada correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Obtiene los valores de la estructura del radicado
   *
   * @param {number} id
   * @return {*}  {Observable<EstructuraRadicado>}
   * @memberof FilingStructureDataService
   */
  getValues(id: number): Observable<EstructuraRadicado> {
    return new Observable(obs => {
      this.http.get<EstructuraRadicado>(this.api.get('estructure', [id])).subscribe(
        structures => {
        obs.next(structures);
      },
      err => {
        obs.error(err);
      });
    });
  }

  /**
   *Elimina una estructura
   *
   * @param {number} id
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  deleteStructure(id: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete<boolean>(this.api.get("estructure", [id])).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Estructura eliminada correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Obtiene los tipos de estructura
   *
   * @return {*}  {Observable<EstructuraRadicado>}
   * @memberof FilingStructureDataService
   */
  getStructureTypes(): Observable<any[]> {
    return new Observable(obs => {
      this.http.get<any[]>(this.api.get('structure_types')).subscribe(
        structures => {
        obs.next(structures);
      },
      err => {
        obs.error(err);
      });
    });
  }

  /**
   *Actualiza la estructura de  radicado
   *
   * @param {EstructuraRadicado} structure
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  updateStructureValues(id: number, structure: EstructuraRadicado): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.put<boolean>(this.api.get("estructure", [id]), structure).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Estructura de radicado actualizada correctamente");
          resolve(true);
        },
        (err) => {
          this.toastr.error("Operación fallida", "No se pudo actualizar la estructura");
          resolve(false);
        }
      );
    });
  }

  /**
   *Obtiene los detalles de la estructura de radicado
   *
   * @return {*}  {Observable<any[]>}
   * @memberof FilingStructureDataService
   */
  getDetails(id: number): Observable<EstructuraRadicado> {
    return new Observable(obs => {
      this.http.get<EstructuraRadicado>(this.api.get('estructure', [id])).subscribe(
        structures => {
        obs.next(structures);
      },
      err => {
        obs.error(err);
      });
    });
  }

  /**
   *Agrega detalle de la estructura
   *
   * @param {*} detail
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  addDetail(detail: any): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.post<boolean>(this.api.get("structure_detail"), detail).subscribe(
        (_resp) => {
          this.toastr.success("Detalle de estructura agregado correctamente", "Operación exitosa");
          resolve(true);
        },
        (err) => {
          this.toastr.error("Detalle de estructurano asignado", "Fallo en la transacción");
          resolve(false);
        }
      );
    });
  }

  /**
   *Actualiza los detalles de una estructura
   *
   * @param {*} detail
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  updateStructureDetails(id: number, detail: any): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.put<boolean>(this.api.get("structure_detail", [id]), detail).subscribe(
        (_resp) => {
          this.toastr.success("Detalle de estructura actualizado correctamente", "Operación exitosa");
          resolve(true);
        },
        (err) => {
          this.toastr.error("No se pudo actualizar el detalle", "Operación fallida");
          resolve(false);
        }
      );
    });
  }

  /**
   *Elimina detalle de la estrura
   *
   * @param {number} id
   * @return {*}  {Promise<boolean>}
   * @memberof FilingStructureDataService
   */
  deleteDetail(id: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.delete<boolean>(this.api.get("structure_detail", [id])).subscribe(
        (_resp) => {
          this.toastr.success("Detalle de estructura eliminado correctamente", "Operación exitosa");
          resolve(true);
        },
        (err) => {
          this.toastr.success("No se pudo eliminar el detalle de la estructura", "Operación fallida");
          resolve(false);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/documents/filing-structure/shared/filing-structure.data.service.ts